import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Play, Disc3, Clock } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { Overline } from "../components/motion";
import { useTranslate } from "../context/TranslateContext";
import { getAlbum, getSong, getPerson, getMovie } from "../mock/data";

export default function AlbumDetail() {
  const { id } = useParams();
  const { t } = useTranslate();
  const a = getAlbum(id);
  useEffect(() => { window.scrollTo(0, 0); }, [id]);
  if (!a) return (<div><InnerNav /><div className="p-20 text-center font-body text-ash">{t("Album not found.")}</div></div>);

  const artist = getPerson(a.artist), movie = a.movie ? getMovie(a.movie) : null;
  const songs = a.songs.map(getSong).filter(Boolean);
  const totalCredits = new Set();
  songs.forEach((s) => [s.singer, s.composer, s.lyricist].forEach((p) => totalCredits.add(p)));
  const listenText = `${a.title}, ${a.type}. Featuring ${songs.length} songs.`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-paper to-surface">
      <InnerNav />
      <section className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-6 py-14 md:px-12 md:py-16 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <div className="overflow-hidden rounded-2xl border border-edge shadow-2xl">
            <img src={a.cover} alt={a.title} data-testid="album-cover" className="aspect-square w-full object-cover" />
          </div>
        </div>
        <div className="lg:col-span-8">
          <Overline>{t(a.type)} · {a.language}</Overline>
          <h1 data-testid="album-title" className="mt-3 font-display text-5xl font-semibold leading-none tracking-tight text-cream md:text-7xl">{a.title}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 font-body text-sm text-ash">
            {artist && <Link to={`/person/${artist.id}`} className="text-gold hover:underline">{artist.name}</Link>}
            <span>{a.year}</span>
            <span className="flex items-center gap-1.5"><Disc3 className="h-4 w-4 text-gold" />{songs.length} {t("songs")}</span>
          </div>
          {movie && (
            <Link to={`/movie/${movie.id}`} className="mt-4 inline-flex items-center gap-2 font-body text-sm text-ash hover:text-gold">
              {t("Soundtrack of")} <span className="text-gold">{movie.title}</span>
            </Link>
          )}
          <div className="mt-6"></div>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-6 pb-28 md:px-12">
        <h2 className="mb-6 font-display text-3xl font-semibold text-cream">{t("Tracklist")}</h2>
        <div className="rounded-2xl border border-edge bg-surface/40" data-testid="album-tracklist">
          {songs.map((s, i) => { const singer = getPerson(s.singer), comp = getPerson(s.composer), lyr = getPerson(s.lyricist); return (
            <div key={s.id} className="flex items-center gap-4 border-b border-edge px-5 py-4 last:border-0" data-testid={`track-${s.id}`}>
              <span className="w-6 text-center font-body text-sm text-ash">{i + 1}</span>
              <Link to={`/song/${s.id}`} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-edge text-gold hover:bg-gold hover:text-ink">
                <Play className="h-3.5 w-3.5 fill-current" />
              </Link>
              <div className="min-w-0 flex-1">
                <Link to={`/song/${s.id}`} className="block truncate font-body text-cream hover:text-gold">{s.title}</Link>
                <div className="truncate font-body text-sm text-ash">
                  {singer && <Link to={`/person/${singer.id}`} className="hover:text-gold">{singer.name}</Link>}
                  {comp && <> · {t("Music")}: <Link to={`/person/${comp.id}`} className="hover:text-gold">{comp.name}</Link></>}
                  {lyr && <> · {t("Lyrics")}: <Link to={`/person/${lyr.id}`} className="hover:text-gold">{lyr.name}</Link></>}
                </div>
              </div>
              <span className="flex items-center gap-1.5 font-body text-sm text-ash"><Clock className="h-3.5 w-3.5" />{s.duration}</span>
            </div>
          ); })}
        </div>
      </div>
    </div>
  );
}
